// components/IdentityPanel.jsx: M3-9 identity editor.
//
// Edits the persisted Identity (display_name + avatar_color). Always
// visible while the user is not in a room. The 8 avatar swatches are
// real <button>s, keyboard + screen reader work out of the box. The
// name input enforces NAME_MAX via maxLength and trims on blur.
// Inline (not toast) error if the trimmed value is empty.

import { useEffect, useState, useCallback, useRef } from 'preact/hooks';
import { getOrCreateIdentity, updateIdentity, NAME_MAX } from '../identity/identity.js';
import { AVATAR_COLORS, AVATAR_COLOR_KEYS } from '../identity/avatar-colors.js';
import { AvatarCircle } from './AvatarCircle.jsx';
import '../styles/identity.css';

/**
 * @param {{ onChange?: (id: any) => void }} props
 */
export function IdentityPanel({ onChange }) {
  /** @type {[any, (id:any)=>void]} */
  const [identity, setIdentity] = useState(/** @type {any} */ (null));
  /** @type {[string, (s:string)=>void]} */
  const [nameDraft, setNameDraft] = useState('');
  const [nameError, setNameError] = useState(/** @type {string|null} */ (null));
  const initialized = useRef(false);

  // Lazy init on mount, getOrCreateIdentity touches localStorage, so we
  // only call it once (and only client-side; the module guards SSR).
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const id = getOrCreateIdentity();
    setIdentity(id);
    setNameDraft(id.display_name);
    if (onChange) onChange(id);
  }, [onChange]);

  const commitName = useCallback(() => {
    if (!identity) return;
    const trimmed = nameDraft.trim();
    if (trimmed.length === 0) {
      setNameError("Name can't be empty");
      return;
    }
    setNameError(null);
    if (trimmed === identity.display_name) return;
    const next = updateIdentity({ display_name: trimmed });
    setIdentity(next);
    setNameDraft(next.display_name);
    if (onChange) onChange(next);
  }, [identity, nameDraft, onChange]);

  const handleNameInput = useCallback((e) => {
    const v = /** @type {HTMLInputElement} */ (e.target).value;
    setNameDraft(v);
    if (nameError && v.trim().length > 0) setNameError(null);
  }, [nameError]);

  const handleNameKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitName();
    }
  }, [commitName]);

  const pickColor = useCallback((colorKey) => {
    if (!identity) return;
    if (identity.avatar_color === colorKey) return;
    const next = updateIdentity({ avatar_color: colorKey });
    setIdentity(next);
    if (onChange) onChange(next);
  }, [identity, onChange]);

  if (!identity) {
    return <section class="identity-panel" data-testid="identity-panel">
      <div class="identity-loading">Loading identity…</div>
    </section>;
  }

  return (
    <section class="identity-panel" data-testid="identity-panel" aria-labelledby="identity-heading">
      <h2 id="identity-heading" class="identity-heading">Your identity</h2>
      <div class="identity-row">
        <AvatarCircle color={identity.avatar_color} name={nameDraft || identity.display_name} size={56} />
        <div class="identity-fields">
          <label class="identity-label" for="identity-name-input">Name</label>
          <input
            id="identity-name-input"
            class="identity-name-input"
            data-testid="identity-name-input"
            type="text"
            value={nameDraft}
            maxLength={NAME_MAX}
            autoComplete="off"
            spellcheck={false}
            onInput={handleNameInput}
            onBlur={commitName}
            onKeyDown={handleNameKeyDown}
            aria-describedby={nameError ? 'identity-name-error' : undefined}
            aria-invalid={nameError ? 'true' : 'false'}
          />
          {nameError && (
            <div id="identity-name-error" class="identity-name-error" role="alert" data-testid="identity-name-error">
              {nameError}
            </div>
          )}
          <div class="identity-counter" aria-live="polite">
            {nameDraft.length}/{NAME_MAX}
          </div>
        </div>
      </div>
      <fieldset class="identity-colors" aria-labelledby="identity-colors-legend">
        <legend id="identity-colors-legend" class="identity-label">Avatar color</legend>
        <div class="identity-color-row" role="radiogroup" aria-label="Avatar color">
          {AVATAR_COLOR_KEYS.map((key) => {
            const selected = identity.avatar_color === key;
            const meta = AVATAR_COLORS[key];
            return (
              <button
                key={key}
                type="button"
                class={selected ? 'identity-color-swatch selected' : 'identity-color-swatch'}
                role="radio"
                aria-checked={selected}
                aria-label={meta.label}
                data-color-key={key}
                data-testid={`identity-color-${key}`}
                onClick={() => pickColor(key)}
                style={{ background: meta.hex }}
              >
                <span class="visually-hidden">{meta.label}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
    </section>
  );
}
